import React, { useState, useEffect } from 'react';
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CTable,
  CSpinner,
  CButton,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CForm,
  CFormInput,
  CFormLabel,
  CAlert,
  CInputGroup,
  CInputGroupText
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { 
  cilPeople, cilPlus, cilUser, cilEnvelopeClosed, cilLocationPin, 
  cilHome, cilCheckCircle, cilCart, cilInfo, cilSearch, 
  cilPencil, cilTrash
} from '@coreui/icons';
import { resellerAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newClientId, setNewClientId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    client_full_name: '',
    client_owner_email: '',
    invoice_owner: '',
    reseller_when_sold: '',
    reseller_contact_full_name: '',
    reseller_contact_email: '',
    billing_street: '',
    billing_city: '',
    billing_state: '',
    billing_country: '',
    billing_zipcode: ''
  });

  const navigate = useNavigate();

  // Fixed Highlight component
  const Highlight = ({ text, highlight }) => {
    // Handle all falsy values except 0 and false
    const safeText = text === null || text === undefined || text === '' 
      ? '' 
      : String(text);
    
    if (!highlight || !highlight.trim() || !safeText) {
      return <>{safeText}</>;
    }
    
    try {
      const searchRegex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const parts = safeText.split(searchRegex);
      
      return (
        <>
          {parts.map((part, i) =>
            searchRegex.test(part) ? <mark key={i}>{part}</mark> : part
          )}
        </>
      );
    } catch (error) {
      // Fallback if regex creation fails
      console.error('Highlight regex error:', error);
      return <>{safeText}</>;
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    filterClients();
  }, [searchTerm, clients]);

  const filterClients = () => {
    let filtered = clients;

    if (searchTerm) {
      filtered = filtered.filter(client =>
        (client.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.client_full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.client_owner_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.billing_city || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.billing_state || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.billing_country || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.billing_street || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.billing_zipcode || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredClients(filtered);
  };

  const fetchClients = async () => {
    try {
      const response = await resellerAPI.getMyClients();
      if (response.data.success) {
        setClients(response.data.clients);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await resellerAPI.createClient(formData);
      if (response.data.success) {
        const clientId = response.data.client.id;
        setNewClientId(clientId);
        setSuccess('Client created successfully!');
        setShowModal(false);
        setShowSuccessModal(true);
        fetchClients();
      }
    } catch (error) {
      console.error('Error creating client:', error);
      setError(error.response?.data?.error || 'Failed to create client');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name || '',
      client_full_name: client.client_full_name || '',
      client_owner_email: client.client_owner_email || '',
      invoice_owner: client.invoice_owner || '',
      reseller_when_sold: client.reseller_when_sold || '',
      reseller_contact_full_name: client.reseller_contact_full_name || '',
      reseller_contact_email: client.reseller_contact_email || '',
      billing_street: client.billing_street || '',
      billing_city: client.billing_city || '',
      billing_state: client.billing_state || '',
      billing_country: client.billing_country || '',
      billing_zipcode: client.billing_zipcode || ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await resellerAPI.updateClient(selectedClient.id, formData);
      if (response.data.success) {
        setSuccess('Client updated successfully!');
        setShowEditModal(false);
        fetchClients();
      }
    } catch (error) {
      console.error('Error updating client:', error);
      setError(error.response?.data?.error || 'Failed to update client');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (client) => {
    setClientToDelete(client);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError('');

    try {
      const response = await resellerAPI.deleteClient(clientToDelete.id);
      if (response.data.success) {
        setSuccess('Client deleted successfully!');
        setShowDeleteModal(false);
        fetchClients();
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      setError(error.response?.data?.error || 'Failed to delete client');
    } finally {
      setDeleting(false);
      setClientToDelete(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError('');
    setSuccess('');
    setFormData({
      name: '',
      client_full_name: '',
      client_owner_email: '',
      invoice_owner: '',
      reseller_when_sold: '',
      reseller_contact_full_name: '',
      reseller_contact_email: '',
      billing_street: '',
      billing_city: '',
      billing_state: '',
      billing_country: '',
      billing_zipcode: ''
    });
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedClient(null);
    setError('');
    setSuccess('');
    setFormData({
      name: '',
      client_full_name: '',
      client_owner_email: '',
      invoice_owner: '',
      reseller_when_sold: '',
      reseller_contact_full_name: '',
      reseller_contact_email: '',
      billing_street: '',
      billing_city: '',
      billing_state: '',
      billing_country: '',
      billing_zipcode: ''
    });
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setClientToDelete(null);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setNewClientId(null);
  };

  const handleAddProducts = () => {
    if (newClientId) {
      navigate(`/client/${newClientId}/products`);
    }
    setShowSuccessModal(false);
  };

  const handleViewClient = () => {
    if (newClientId) {
      navigate(`/client/${newClientId}`);
    }
    setShowSuccessModal(false);
  };

  const handleOpenDetails = (client) => {
    setSelectedClient(client);
    setShowDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedClient(null);
  };

  const handleAddProductsFromDetails = () => {
    if (selectedClient) {
      navigate(`/client/${selectedClient.id}/products`);
    }
    handleCloseDetails();
  };

  if (loading) {
    return (
      <CContainer className="d-flex justify-content-center align-items-center min-vh-50">
        <CSpinner color="primary" />
      </CContainer>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>My Clients</h2>
        <CButton color="primary" onClick={() => setShowModal(true)}>
          <CIcon icon={cilPlus} className="me-2" />
          Add Client
        </CButton>
      </div>

      {/* Success Alert */}
      {success && (
        <CAlert color="success" className="mb-4" dismissible onClose={() => setSuccess('')}>
          {success}
        </CAlert>
      )}

      {/* Error Alert */}
      {error && (
        <CAlert color="danger" className="mb-4" dismissible onClose={() => setError('')}>
          {error}
        </CAlert>
      )}

      <CCard>
        <CCardHeader>
          <CRow className="align-items-center">
            <CCol md={6}>
              <h5 className="mb-0">Client List ({filteredClients.length} clients)</h5>
            </CCol>
            <CCol md={6}>
              <CInputGroup>
                <CInputGroupText>
                  <CIcon icon={cilSearch} />
                </CInputGroupText>
                <CFormInput
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <CButton 
                    color="secondary" 
                    variant="outline" 
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </CButton>
                )}
              </CInputGroup>
            </CCol>
          </CRow>
        </CCardHeader>
        <CCardBody>
          {filteredClients.length > 0 ? (
            <div className="table-responsive">
              <CTable hover>
                <thead>
                  <tr>
                    <th>Client Name</th>
                    <th>Contact Person</th>
                    <th>Email</th>
                    <th>Location</th>
                    <th>Billing Address</th>
                    <th width="300px">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr key={client.id}>
                      <td>
                        <strong><Highlight text={client.name || ''} highlight={searchTerm} /></strong>
                      </td>
                      <td><Highlight text={client.client_full_name || ''} highlight={searchTerm} /></td>
                      <td>
                        {client.client_owner_email ? (
                          <a href={`mailto:${client.client_owner_email}`}>
                            <Highlight text={client.client_owner_email || ''} highlight={searchTerm} />
                          </a>
                        ) : (
                          <Highlight text={''} highlight={searchTerm} />
                        )}
                      </td>
                      <td>
                        <Highlight 
                          text={`${client.billing_city || ''}, ${client.billing_state || ''}`} 
                          highlight={searchTerm} 
                        />
                        <br />
                        <small className="text-medium-emphasis">
                          <Highlight text={client.billing_country || ''} highlight={searchTerm} />
                        </small>
                      </td>
                      <td>
                        <small>
                          <Highlight text={client.billing_street || ''} highlight={searchTerm} /><br />
                          <Highlight 
                            text={`${client.billing_city || ''}, ${client.billing_state || ''} ${client.billing_zipcode || ''}`} 
                            highlight={searchTerm} 
                          />
                        </small>
                      </td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          <CButton 
                            color="info" 
                            size="sm" 
                            onClick={() => handleOpenDetails(client)}
                            title="View Details"
                          >
                            <CIcon icon={cilInfo} />
                          </CButton>
                          <CButton 
                            color="warning" 
                            size="sm" 
                            onClick={() => handleEdit(client)}
                            title="Edit Client"
                          >
                            <CIcon icon={cilPencil} />
                          </CButton>
                          <CButton 
                            color="primary" 
                            size="sm" 
                            onClick={() => navigate(`/client/${client.id}/products`, { state: { client } })}
                            title="Add Products"
                          >
                            <CIcon icon={cilCart} />
                          </CButton>
                          <CButton 
                            color="danger" 
                            size="sm" 
                            onClick={() => handleDeleteClick(client)}
                            title="Delete Client"
                          >
                            <CIcon icon={cilTrash} />
                          </CButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </CTable>
            </div>
          ) : (
            <div className="text-center py-5">
              <CIcon icon={cilPeople} size="3xl" className="text-muted mb-3" />
              <h4 className="text-muted">{searchTerm ? `No clients found matching "${searchTerm}"` : 'No clients found'}</h4>
              <p>{searchTerm ? 'Try adjusting your search terms.' : 'You don\'t have any clients assigned to your reseller account.'}</p>
              {searchTerm && (
                <CButton color="primary" onClick={() => setSearchTerm('')} className="mb-3">
                  Clear Search
                </CButton>
              )}
            </div>
          )}
        </CCardBody>
      </CCard>

      {/* Add Client Modal */}
      <CModal 
        size="lg" 
        visible={showModal} 
        onClose={handleCloseModal}
        backdrop="static"
        keyboard={false}
      >
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilUser} className="me-2" />
            Add New Client
          </CModalTitle>
        </CModalHeader>
        <CForm onSubmit={handleSubmit}>
          <CModalBody>
            {error && (
              <CAlert color="danger" className="mb-3">
                {error}
              </CAlert>
            )}

            <CRow className="g-3">
              <CCol md={6}>
                <CFormLabel htmlFor="name">Company Name *</CFormLabel>
                <CFormInput
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter company name"
                  required
                />
              </CCol>
              <CCol md={6}>
                <CFormLabel htmlFor="client_full_name">Contact Person *</CFormLabel>
                <CFormInput
                  type="text"
                  id="client_full_name"
                  name="client_full_name"
                  value={formData.client_full_name}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  required
                />
              </CCol>
              <CCol md={12}>
                <CFormLabel htmlFor="client_owner_email">Email Address *</CFormLabel>
                <CInputGroup>
                  <CInputGroupText>
                    <CIcon icon={cilEnvelopeClosed} />
                  </CInputGroupText>
                  <CFormInput
                    type="email"
                    id="client_owner_email"
                    name="client_owner_email"
                    value={formData.client_owner_email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    required
                  />
                </CInputGroup>
              </CCol>

              <CCol md={6}>
                <CFormLabel htmlFor="invoice_owner">Invoice Owner</CFormLabel>
                <CFormInput
                  type="text"
                  id="invoice_owner"
                  name="invoice_owner"
                  value={formData.invoice_owner}
                  onChange={handleInputChange}
                  placeholder="Billing account name"
                />
              </CCol>
              <CCol md={6}>
                <CFormLabel htmlFor="reseller_when_sold">Distributor Reseller</CFormLabel>
                <CFormInput
                  type="text"
                  id="reseller_when_sold"
                  name="reseller_when_sold"
                  value={formData.reseller_when_sold}
                  onChange={handleInputChange}
                  placeholder="If sold via distributor"
                />
              </CCol>

              <CCol md={6}>
                <CFormLabel htmlFor="reseller_contact_full_name">Reseller Contact Name</CFormLabel>
                <CFormInput
                  type="text"
                  id="reseller_contact_full_name"
                  name="reseller_contact_full_name"
                  value={formData.reseller_contact_full_name}
                  onChange={handleInputChange}
                  placeholder="Contact person at your company"
                />
              </CCol>
              <CCol md={6}>
                <CFormLabel htmlFor="reseller_contact_email">Reseller Contact Email</CFormLabel>
                <CFormInput
                  type="email"
                  id="reseller_contact_email"
                  name="reseller_contact_email"
                  value={formData.reseller_contact_email}
                  onChange={handleInputChange}
                  placeholder="Contact email at your company"
                />
              </CCol>

              <CCol md={12}>
                <CFormLabel htmlFor="billing_street">Street Address</CFormLabel>
                <CInputGroup>
                  <CInputGroupText>
                    <CIcon icon={cilHome} />
                  </CInputGroupText>
                  <CFormInput
                    type="text"
                    id="billing_street"
                    name="billing_street"
                    value={formData.billing_street}
                    onChange={handleInputChange}
                    placeholder="Enter street address"
                  />
                </CInputGroup>
              </CCol>
              <CCol md={4}>
                <CFormLabel htmlFor="billing_city">City</CFormLabel>
                <CInputGroup>
                  <CInputGroupText>
                    <CIcon icon={cilLocationPin} />
                  </CInputGroupText>
                  <CFormInput
                    type="text"
                    id="billing_city"
                    name="billing_city"
                    value={formData.billing_city}
                    onChange={handleInputChange}
                    placeholder="City"
                  />
                </CInputGroup>
              </CCol>
              <CCol md={4}>
                <CFormLabel htmlFor="billing_state">State/Province</CFormLabel>
                <CFormInput
                  type="text"
                  id="billing_state"
                  name="billing_state"
                  value={formData.billing_state}
                  onChange={handleInputChange}
                  placeholder="State"
                />
              </CCol>
              <CCol md={4}>
                <CFormLabel htmlFor="billing_zipcode">ZIP/Postal Code</CFormLabel>
                <CFormInput
                  type="text"
                  id="billing_zipcode"
                  name="billing_zipcode"
                  value={formData.billing_zipcode}
                  onChange={handleInputChange}
                  placeholder="ZIP code"
                />
              </CCol>
              <CCol md={12}>
                <CFormLabel htmlFor="billing_country">Country</CFormLabel>
                <CFormInput
                  type="text"
                  id="billing_country"
                  name="billing_country"
                  value={formData.billing_country}
                  onChange={handleInputChange}
                  placeholder="Country"
                />
              </CCol>
            </CRow>

            <div className="mt-3">
              <small className="text-muted">* Required fields</small>
            </div>
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" onClick={handleCloseModal} disabled={submitting}>
              Cancel
            </CButton>
            <CButton type="submit" color="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                <>
                  <CIcon icon={cilPlus} className="me-2" />
                  Create Client
                </>
              )}
            </CButton>
          </CModalFooter>
        </CForm>
      </CModal>

      {/* Edit Client Modal */}
      <CModal 
        size="lg" 
        visible={showEditModal} 
        onClose={handleCloseEditModal}
        backdrop="static"
        keyboard={false}
      >
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilPencil} className="me-2" />
            Edit Client: {selectedClient?.name}
          </CModalTitle>
        </CModalHeader>
        <CForm onSubmit={handleUpdate}>
          <CModalBody>
            {error && (
              <CAlert color="danger" className="mb-3">
                {error}
              </CAlert>
            )}

            <CRow className="g-3">
              <CCol md={6}>
                <CFormLabel htmlFor="edit_name">Company Name *</CFormLabel>
                <CFormInput
                  type="text"
                  id="edit_name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter company name"
                  required
                />
              </CCol>
              <CCol md={6}>
                <CFormLabel htmlFor="edit_client_full_name">Contact Person *</CFormLabel>
                <CFormInput
                  type="text"
                  id="edit_client_full_name"
                  name="client_full_name"
                  value={formData.client_full_name}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  required
                />
              </CCol>
              <CCol md={12}>
                <CFormLabel htmlFor="edit_client_owner_email">Email Address *</CFormLabel>
                <CInputGroup>
                  <CInputGroupText>
                    <CIcon icon={cilEnvelopeClosed} />
                  </CInputGroupText>
                  <CFormInput
                    type="email"
                    id="edit_client_owner_email"
                    name="client_owner_email"
                    value={formData.client_owner_email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    required
                  />
                </CInputGroup>
              </CCol>

              <CCol md={6}>
                <CFormLabel htmlFor="edit_invoice_owner">Invoice Owner</CFormLabel>
                <CFormInput
                  type="text"
                  id="edit_invoice_owner"
                  name="invoice_owner"
                  value={formData.invoice_owner}
                  onChange={handleInputChange}
                  placeholder="Billing account name"
                />
              </CCol>
              <CCol md={6}>
                <CFormLabel htmlFor="edit_reseller_when_sold">Distributor Reseller</CFormLabel>
                <CFormInput
                  type="text"
                  id="edit_reseller_when_sold"
                  name="reseller_when_sold"
                  value={formData.reseller_when_sold}
                  onChange={handleInputChange}
                  placeholder="If sold via distributor"
                />
              </CCol>

              <CCol md={6}>
                <CFormLabel htmlFor="edit_reseller_contact_full_name">Reseller Contact Name</CFormLabel>
                <CFormInput
                  type="text"
                  id="edit_reseller_contact_full_name"
                  name="reseller_contact_full_name"
                  value={formData.reseller_contact_full_name}
                  onChange={handleInputChange}
                  placeholder="Contact person at your company"
                />
              </CCol>
              <CCol md={6}>
                <CFormLabel htmlFor="edit_reseller_contact_email">Reseller Contact Email</CFormLabel>
                <CFormInput
                  type="email"
                  id="edit_reseller_contact_email"
                  name="reseller_contact_email"
                  value={formData.reseller_contact_email}
                  onChange={handleInputChange}
                  placeholder="Contact email at your company"
                />
              </CCol>

              <CCol md={12}>
                <CFormLabel htmlFor="edit_billing_street">Street Address</CFormLabel>
                <CInputGroup>
                  <CInputGroupText>
                    <CIcon icon={cilHome} />
                  </CInputGroupText>
                  <CFormInput
                    type="text"
                    id="edit_billing_street"
                    name="billing_street"
                    value={formData.billing_street}
                    onChange={handleInputChange}
                    placeholder="Enter street address"
                  />
                </CInputGroup>
              </CCol>
              <CCol md={4}>
                <CFormLabel htmlFor="edit_billing_city">City</CFormLabel>
                <CInputGroup>
                  <CInputGroupText>
                    <CIcon icon={cilLocationPin} />
                  </CInputGroupText>
                  <CFormInput
                    type="text"
                    id="edit_billing_city"
                    name="billing_city"
                    value={formData.billing_city}
                    onChange={handleInputChange}
                    placeholder="City"
                  />
                </CInputGroup>
              </CCol>
              <CCol md={4}>
                <CFormLabel htmlFor="edit_billing_state">State/Province</CFormLabel>
                <CFormInput
                  type="text"
                  id="edit_billing_state"
                  name="billing_state"
                  value={formData.billing_state}
                  onChange={handleInputChange}
                  placeholder="State"
                />
              </CCol>
              <CCol md={4}>
                <CFormLabel htmlFor="edit_billing_zipcode">ZIP/Postal Code</CFormLabel>
                <CFormInput
                  type="text"
                  id="edit_billing_zipcode"
                  name="billing_zipcode"
                  value={formData.billing_zipcode}
                  onChange={handleInputChange}
                  placeholder="ZIP code"
                />
              </CCol>
              <CCol md={12}>
                <CFormLabel htmlFor="edit_billing_country">Country</CFormLabel>
                <CFormInput
                  type="text"
                  id="edit_billing_country"
                  name="billing_country"
                  value={formData.billing_country}
                  onChange={handleInputChange}
                  placeholder="Country"
                />
              </CCol>
            </CRow>

            <div className="mt-3">
              <small className="text-muted">* Required fields</small>
            </div>
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" onClick={handleCloseEditModal} disabled={submitting}>
              Cancel
            </CButton>
            <CButton type="submit" color="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  Updating...
                </>
              ) : (
                <>
                  <CIcon icon={cilPencil} className="me-2" />
                  Update Client
                </>
              )}
            </CButton>
          </CModalFooter>
        </CForm>
      </CModal>

      {/* Delete Confirmation Modal */}
      <CModal 
        visible={showDeleteModal} 
        onClose={handleCloseDeleteModal}
        backdrop="static"
        keyboard={false}
      >
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilTrash} className="me-2 text-danger" />
            Delete Client
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <div className="text-center">
            <CIcon icon={cilTrash} size="3xl" className="text-danger mb-3" />
            <h5>Are you sure you want to delete this client?</h5>
            <p className="text-medium-emphasis">
              You are about to delete <strong>{clientToDelete?.name}</strong>. 
              This action cannot be undone and all associated data will be permanently removed.
            </p>
          </div>
        </CModalBody>
        <CModalFooter className="justify-content-center">
          <CButton color="secondary" onClick={handleCloseDeleteModal} disabled={deleting}>
            Cancel
          </CButton>
          <CButton color="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? (
              <>
                <CSpinner size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <CIcon icon={cilTrash} className="me-2" />
                Delete Client
              </>
            )}
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Client Details Modal */}
      <CModal 
        size="lg" 
        visible={showDetailsModal} 
        onClose={handleCloseDetails}
        backdrop="static"
        keyboard={false}
      >
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilInfo} className="me-2" />
            Client Details: {selectedClient?.name}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selectedClient && (
            <CRow className="g-3">
              <CCol md={6}>
                <strong>Company Name</strong>
                <p className="mb-0">{selectedClient.name || 'N/A'}</p>
              </CCol>
              <CCol md={6}>
                <strong>Contact Person</strong>
                <p className="mb-0">{selectedClient.client_full_name || 'N/A'}</p>
              </CCol>
              <CCol md={6}>
                <strong>Email</strong>
                <p className="mb-0">
                  <a href={`mailto:${selectedClient.client_owner_email}`}>
                    {selectedClient.client_owner_email || 'N/A'}
                  </a>
                </p>
              </CCol>
              <CCol md={6}>
                <strong>Invoice Owner</strong>
                <p className="mb-0">{selectedClient.invoice_owner || 'N/A'}</p>
              </CCol>
              <CCol md={6}>
                <strong>Distributor Reseller</strong>
                <p className="mb-0">{selectedClient.reseller_when_sold || 'N/A'}</p>
              </CCol>
              <CCol md={6}>
                <strong>Reseller Contact Name</strong>
                <p className="mb-0">{selectedClient.reseller_contact_full_name || 'N/A'}</p>
              </CCol>
              <CCol md={6}>
                <strong>Reseller Contact Email</strong>
                <p className="mb-0">
                  <a href={`mailto:${selectedClient.reseller_contact_email}`}>
                    {selectedClient.reseller_contact_email || 'N/A'}
                  </a>
                </p>
              </CCol>
              <CCol md={6}>
                <strong>ResellerID</strong>
                <p className="mb-0">{selectedClient.resellerid || 'N/A'}</p>
              </CCol>
              <CCol md={6}>
                <strong>Reseller Name</strong>
                <p className="mb-0">{selectedClient.resellername || 'N/A'}</p>
              </CCol>
              <CCol md={6}>
                <strong>Client ID</strong>
                <p className="mb-0">{selectedClient.clientid || 'N/A'}</p>
              </CCol>

              <CCol md={12}>
                <strong>Billing Address</strong>
                <p className="mb-0">
                  {selectedClient.billing_street || 'N/A'}<br />
                  {selectedClient.billing_city || 'N/A'}, {selectedClient.billing_state || 'N/A'} {selectedClient.billing_zipcode || 'N/A'}<br />
                  {selectedClient.billing_country || 'N/A'}
                </p>
              </CCol>
              <CCol md={6}>
                <strong>Created Date</strong>
                <p className="mb-0">
                  {selectedClient.created_date && selectedClient.created_date.$date 
                    ? new Date(selectedClient.created_date.$date).toLocaleDateString() 
                    : 'N/A'}
                </p>
              </CCol>
              <CCol md={6}>
                <strong>Updated Date</strong>
                <p className="mb-0">
                  {selectedClient.updated_date && selectedClient.updated_date.$date 
                    ? new Date(selectedClient.updated_date.$date).toLocaleDateString() 
                    : 'N/A'}
                </p>
              </CCol>
            </CRow>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="primary" onClick={handleAddProductsFromDetails}>
            <CIcon icon={cilCart} className="me-2" />
            Add Products
          </CButton>
          <CButton color="secondary" onClick={handleCloseDetails}>
            Close
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Success Confirmation Modal */}
      <CModal 
        visible={showSuccessModal} 
        onClose={handleCloseSuccessModal}
        backdrop="static"
        keyboard={false}
      >
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilCheckCircle} className="me-2 text-success" />
            Client Created Successfully!
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <div className="text-center">
            <CIcon icon={cilCheckCircle} size="3xl" className="text-success mb-3" />
            <h5>Client has been created successfully!</h5>
            <p className="text-medium-emphasis">
              What would you like to do next?
            </p>
          </div>
        </CModalBody>
        <CModalFooter className="justify-content-center">
          <CButton color="primary" onClick={handleAddProducts}>
            <CIcon icon={cilCart} className="me-2" />
            Add Products
          </CButton>
          <CButton color="secondary" onClick={handleViewClient}>
            <CIcon icon={cilUser} className="me-2" />
            View Client Details
          </CButton>
          <CButton color="outline-secondary" onClick={handleCloseSuccessModal}>
            Close
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
};

export default Clients;